import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import NotFoundMessage from "../../components/common/Modal/NotFoundMessage";
import LoadingComponent from "../../components/common/LoadingComponent";
import { Icons } from "../../constants/icon-library";
import {
  getParticipants,
  removeParticipant,
} from "../../features/participant/participantSlice";
import { confirmationModal } from "../../constants/confirmation.constants";
import { toast } from "react-toastify";
import { formatPlatformName } from "../../constants/constants";

const PAGE_SIZE = 30;

function Participants() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    participants = [],
    pagination = {},
    loading,
  } = useSelector((state) => state.participants);

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = pagination?.totalPages || 1;

  useEffect(() => {
    dispatch(getParticipants({ page: currentPage, limit: PAGE_SIZE }));
  }, [dispatch, currentPage]);

  const handleDelete = async (uuid) => {
    const result = await confirmationModal({
      title: "Are you sure?",
      text: "This will permanently delete the assistant.",
    });

    if (result.isConfirmed) {
      try {
        await dispatch(removeParticipant(uuid)).unwrap();
        toast.success("Participant deleted successfully.");
        dispatch(getParticipants({ page: currentPage, limit: PAGE_SIZE }));
      } catch {
        toast.error("Failed to delete participant.");
      }
    }
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const showNotFoundError = !loading && participants.length === 0;

  return (
    <div className="content">
      <div className="d-flex flex-column pt-3">
        {!showNotFoundError && (
          <div className="row">
            <div className="card border p-0">
              <div className="card-header p-4 py-3 border-bottom bg-body">
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="mb-0">Participants</h3>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate("create")}
                  >
                    <i
                      className={`wb ${Icons("add")} fs-8 me-2 align-middle`}
                    />
                    Add Participant
                  </button>
                </div>
              </div>

              <div className="card-body">
                {loading ? (
                  <LoadingComponent loading />
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0 fs-9">
                        <thead>
                          <tr>
                            <th className="ps-3 fw-semibold text-muted">
                              Name
                            </th>
                            <th className="fw-semibold text-muted">
                              Email / UserName
                            </th>
                            <th className="fw-semibold text-muted">Platform</th>
                            <th className="fw-semibold text-muted">
                              Created On
                            </th>
                            <th className="fw-semibold text-muted text-center">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {participants.map((p) => (
                            <tr key={p.uuid}>
                              <td className="ps-3">{p.name || "-"}</td>
                              <td className="text-muted">
                                {p.username || "-"}
                              </td>
                              <td>
                                <span className="fw-normal">
                                  {formatPlatformName(p.platforms || "-")}
                                </span>
                              </td>
                              <td className="text-muted">
                                {moment
                                  .utc(p.createdAt)
                                  .format("MMM D, YYYY • h:mm A")}
                              </td>
                              <td className="text-center">
                                <button
                                  className="btn btn-sm btn-primary me-1"
                                  onClick={() =>
                                    navigate(`${p.uuid}`, {
                                      state: { participant: p },
                                    })
                                  }
                                >
                                  <i
                                    className={`wb ${Icons("edit")} fs-8 align-middle`}
                                  />
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDelete(p.uuid)}
                                >
                                  <i
                                    className={`wb ${Icons("delete")} fs-8 align-middle`}
                                  />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 px-1">
                        <small className="text-muted">
                          Showing {(pagination.page - 1) * pagination.limit + 1}
                          –
                          {Math.min(
                            pagination.page * pagination.limit,
                            pagination.total,
                          )}{" "}
                          of {pagination.total} participants
                        </small>

                        <ul className="pagination pagination-sm mb-0">
                          <li
                            className={`page-item ${
                              pagination.page === 1 ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() =>
                                handlePageChange(pagination.page - 1)
                              }
                            >
                              &laquo;
                            </button>
                          </li>

                          {Array.from(
                            { length: totalPages },
                            (_, i) => i + 1,
                          ).map((page) => (
                            <li
                              key={page}
                              className={`page-item ${
                                pagination.page === page ? "active" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(page)}
                              >
                                {page}
                              </button>
                            </li>
                          ))}

                          <li
                            className={`page-item ${
                              pagination.page === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() =>
                                handlePageChange(pagination.page + 1)
                              }
                            >
                              &raquo;
                            </button>
                          </li>
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {showNotFoundError && (
          <NotFoundMessage
            message="No participants found. Add a participant to get started."
            onAddNew={() => navigate("create")}
            buttonMessage="Add Participant"
          />
        )}
      </div>
    </div>
  );
}

export default Participants;
